import { InfoListItem } from '@brightlayer-ui/react-components';
import { Box, Button, Divider, IconButton, Paper, Stack, Tooltip, Typography } from '@mui/material';
import React, { useMemo, type JSX } from 'react';
import clsx from 'clsx';
import { useNavigate } from 'react-router-dom';
import { WidgetHeader } from 'pages/WidgetManagement/common/WidgetHeader';
import { WidgetFooter } from 'pages/WidgetManagement/common/WidgetFooter';
import { CustomTransComponent, GroupPath } from 'components';
import { CustomEmptyState } from 'components/CustomEmptyState';
import { Add, ChevronRight, DesignServices, DynamicForm, JoinFull, JoinInner, Link } from '@mui/icons-material';
// import { useRBAC } from 'hooks';
// import { useTypedSelector } from '@fiji/common';
// import { selectCurrentPermission } from '@fiji/common/src/features/profile/profileSlice';
import { Ups } from '@brightlayer-ui/icons-mui';
import { CustomSwitchBtn, type GroupState } from 'components/CustomSwitchBtn/CustomSwitchBtn';
import { useTranslation } from 'react-i18next';
import { useNewCustomTranslations, useRBAC } from 'hooks';
import { selectCurrentPermission } from '@fiji/common/src/features/profile/profileSlice';
import { useTypedSelector } from '@fiji/common';

interface LoadGroupsWidgetProps {
    widgetData: any;
    mode: string;
    onLoadGroupToggle?: (loadGroup: any, newState: GroupState, widgetId: string) => Promise<void> | void;
    isLoadGroupUpdating?: boolean;
    loadGroupStates?: Record<string, GroupState>;
    onOpenLoadGroupsModal?: (args: { widgetId: string; isMultiDevice: boolean; initialSelectedIds: string[] }) => void;
    isWidgetUpdating?: boolean;
    onCreateLoadGroup?: () => void;
}

const getIsDisabled = (mode?: string, onLoadGroupToggle?: any): boolean =>
    mode === 'configured' || mode === 'preview' || mode === 'edit' || !onLoadGroupToggle;

const Component = ({
    widgetData: detailWidget,
    mode,
    onLoadGroupToggle,
    isLoadGroupUpdating = false,
    loadGroupStates = {},
    onOpenLoadGroupsModal,
    isWidgetUpdating = false,
    onCreateLoadGroup,
}: LoadGroupsWidgetProps): JSX.Element => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    useNewCustomTranslations('WIDGETS');
    const rolePermissions = useTypedSelector(selectCurrentPermission);
    const { hasPermission } = useRBAC(rolePermissions);
    const canViewLoadsGroups = hasPermission('load-groups_view-devices');
    const canViewLoadsGroupsExpired = hasPermission('load-groups_view-devices_hasExpired');
    const canUpdateUserWidget = hasPermission('widget-management_edit-user-widgets');
    const canUpdateSystemWidget = hasPermission('widget-management_edit-system-widgets');
    const canCreateLoadGroups = hasPermission('load-groups_create-devices');

    const handleRedundancyState = (item: any): JSX.Element => {
        if (item?.isGroupRedundancyActive) {
            if (item?.isGroupRedundant === true) {
                return (
                    <Tooltip title={t('WIDGETS:LOADS_GROUPS.REDUNDANCY_ACTIVE')} placement="top">
                        <JoinFull sx={{ color: '#39B620' }} />
                    </Tooltip>
                );
            } else if (item?.isGroupRedundant === false) {
                return (
                    <Tooltip title={t('WIDGETS:LOADS_GROUPS.REDUNDANCY_ERROR')} placement="top">
                        <JoinInner sx={{ color: '#CA3C3D' }} />
                    </Tooltip>
                );
            }
        }
        return <></>;
    };

    const getLoadCount = (item: any): JSX.Element => (
        <Typography sx={{ fontSize: 14, fontWeight: '400', color: '#424e54' }}>{`${item?.numberOfLoadsEnabled ?? 0}/${
            item?.totalLoads ?? 0
        }`}</Typography>
    );
    const isDisabled = getIsDisabled(mode, onLoadGroupToggle);
    const isMultiDeviceWidget = Boolean(detailWidget?.multiDevice);

    const initialSelectedIds = useMemo(() => {
        if (isMultiDeviceWidget) {
            return (detailWidget?.selectedNodes ?? detailWidget?.secondary ?? []).map((item: any) => item?.id);
        }
        return (detailWidget?.secondary ?? []).map((item: any) => item?.id);
    }, [detailWidget?.secondary, detailWidget?.selectedNodes, isMultiDeviceWidget]);

    return (
        <Paper sx={{ height: '100%' }}>
            <WidgetHeader
                mode={mode}
                widgetData={detailWidget}
                sortedData={detailWidget?.secondary}
                count={detailWidget?.secondary?.length}
            />
            {mode !== 'edit' && <Divider />}
            <Stack
                sx={{
                    height: `calc(100% - ${mode === 'edit' || detailWidget?.viewAllButton ? '97px' : '49px'})`,
                    overflow: 'auto',
                }}
            >
                {detailWidget?.secondary?.length ? (
                    detailWidget?.secondary
                        ?.slice(0, detailWidget?.maxCount ?? detailWidget?.secondary?.length)
                        .map((item: any) => (
                            <InfoListItem
                                title={item?.name}
                                subtitle={[
                                    <Stack key={item?.key}>
                                        <Stack flexDirection={'row'} alignItems={'center'} gap={1}>
                                            <Box pt={'4px'}>
                                                {item?.isMultipleDevice === true ? (
                                                    <Link sx={{ color: '#727E84', fontSize: 16 }} />
                                                ) : (
                                                    detailWidget?.multiDevice && (
                                                        <Ups sx={{ color: '#727e84', fontSize: 16 }} />
                                                    )
                                                )}
                                            </Box>
                                            {item?.isMultipleDevice === true ? (
                                                <Typography fontSize={12} fontWeight={300} color="#333D43">
                                                    <CustomTransComponent
                                                        translationKey={'WIDGETS:BALANCING.MULTIPLE_DEVICES'}
                                                    />
                                                </Typography>
                                            ) : (
                                                detailWidget?.multiDevice && (
                                                    <GroupPath
                                                        pathName={item?.groupPath ?? []}
                                                        deviceName={item?.deviceName}
                                                    />
                                                )
                                            )}
                                        </Stack>
                                    </Stack>,
                                ]}
                                hidePadding={true}
                                key={item?.id}
                                divider="full"
                                rightComponent={
                                    <Stack
                                        flexDirection={'row'}
                                        alignItems={'center'}
                                        gap={1}
                                        className={clsx('custom-info-list-item', isDisabled && 'disabled')}
                                    >
                                        <Box pt={'4px'}>{handleRedundancyState(item)}</Box>
                                        {getLoadCount(item)}
                                        <CustomSwitchBtn
                                            id={item?.id}
                                            totalLoadCount={item?.totalLoads}
                                            enabledLoadCount={item?.numberOfLoadsEnabled}
                                            groupState={loadGroupStates[String(item?.id)] ?? item?.groupState}
                                            loadGroupName={item?.name}
                                            disabled={
                                                isDisabled ||
                                                isLoadGroupUpdating ||
                                                loadGroupStates[String(item?.id)] === 'PENDING'
                                            }
                                            onChange={(_id, newState) =>
                                                onLoadGroupToggle?.(item, newState, detailWidget?.id)
                                            }
                                        />
                                        <IconButton
                                            disabled={isDisabled}
                                            onClick={(): void => {
                                                if (detailWidget?.detailRoute && mode === 'view')
                                                    navigate(`${detailWidget?.detailRoute}/${item.id}`);
                                            }}
                                        >
                                            <ChevronRight />
                                        </IconButton>
                                    </Stack>
                                }
                            />
                        ))
                ) : (
                    <CustomEmptyState
                        className="padding-y-96"
                        icon={<DynamicForm className="height-76 width-76 color-icon" />}
                        title={(<CustomTransComponent translationKey={'WIDGETS:LOADS_GROUPS.NO_LOAD_GROUPS'} />) as any}
                        description={
                            <CustomTransComponent translationKey={'WIDGETS:LOADS_GROUPS.NO_LOAD_GROUPS_DESCRIPTION'} />
                        }
                        actions={
                            <Stack gap={2}>
                                {detailWidget?.multiDevice && (canUpdateUserWidget || canUpdateSystemWidget) && (
                                    <Button
                                        variant="contained"
                                        startIcon={<Add />}
                                        disabled={mode !== 'view' || isWidgetUpdating}
                                        onClick={(): void => {
                                            onOpenLoadGroupsModal?.({
                                                widgetId: detailWidget?.id,
                                                isMultiDevice: false,
                                                initialSelectedIds,
                                            });
                                        }}
                                    >
                                        <CustomTransComponent
                                            translationKey={'NOTIFICATION_PREFERENCES:SELECT_LOAD_GROUPS'}
                                        />
                                    </Button>
                                )}
                                {canCreateLoadGroups && (
                                    <Button
                                        disabled={mode !== 'view'}
                                        variant="contained"
                                        startIcon={<DesignServices />}
                                        onClick={(): void => {
                                            if (mode !== 'view') return;
                                            if (onCreateLoadGroup) {
                                                onCreateLoadGroup();
                                                return;
                                            }
                                            if (detailWidget?.detailRoute) {
                                                navigate(detailWidget.detailRoute, {
                                                    state: { openCreateLoadGroup: true },
                                                });
                                            }
                                        }}
                                    >
                                        <CustomTransComponent
                                            translationKey={'WIDGETS:LOADS_GROUPS.CREATE_LOAD_GROUP'}
                                        />
                                    </Button>
                                )}
                            </Stack>
                        }
                    />
                )}
            </Stack>
            {(canViewLoadsGroups || canViewLoadsGroupsExpired) && (
                <WidgetFooter mode={mode} widgetData={detailWidget} />
            )}
        </Paper>
    );
};

export default Component;
